import CreativeService from '@/services/creativeService'
import { useAppStore } from '@/stores/app'
import { hexToRgb } from './colorHelper'
import type { backdrop } from '@/types/backdrop'
import type { background } from '@/types/background'
import { getImageAspectRatio } from '@/helpers/logoHelper'
import { squareAspectRatioBuffer } from '@/constants/canvas'

export const loadHtmlImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

export const getLuminance = (r: number, g: number, b: number): number => {
  const a = [r, g, b].map((v) => {
    v /= 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
}

export const contrastRatio = (rgb1: any, rgb2: any): number => {
  const L1 = getLuminance(rgb1.r, rgb1.g, rgb1.b)
  const L2 = getLuminance(rgb2.r, rgb2.g, rgb2.b)
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05)
}

export const getDominantColor = (htmlImage: HTMLImageElement) => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = htmlImage.width
  canvas.height = htmlImage.height
  ctx.drawImage(htmlImage, 0, 0)
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data

  const colorCounts: { [key: string]: number } = {}
  let maxCount = 0
  let dominantColor = { r: 0, g: 0, b: 0 }

  for (let i = 0; i < imageData.length; i += 4) {
    const r = imageData[i]
    const g = imageData[i + 1]
    const b = imageData[i + 2]
    const a = imageData[i + 3]
    if (a < 255) continue // Skip transparent pixels
    const key = `${r},${g},${b}`
    colorCounts[key] = (colorCounts[key] || 0) + 1
    if (colorCounts[key] > maxCount) {
      maxCount = colorCounts[key]
      dominantColor = { r, g, b }
    }
  }
  return dominantColor
}

export const showGlowLayer = async (
  fabricCanvas: any,
  logoUrl: string | undefined,
  bgColor = '#ffffff',
  item: backdrop | background | null = null
) => {
  let isRectangleLogo = false
  try {
    const logoAspectRatio = await getImageAspectRatio(logoUrl || '')
    if (logoAspectRatio >= squareAspectRatioBuffer) {
      isRectangleLogo = true
    }
  } catch (e) {
    console.error('Error calculating the aspect ratio of logo:', e)
  }
  const primaryGlowObj = isRectangleLogo ? fabricCanvas?.getObjects().find((obj: any) => obj.layerName === 'Glow - Horizontal') : fabricCanvas?.getObjects().find((obj: any) => obj.layerName === 'Glow')
  const secondaryGlowObject = (!isRectangleLogo) ? fabricCanvas?.getObjects().find((obj: any) => obj.layerName === 'Glow - Horizontal') : fabricCanvas?.getObjects().find((obj: any) => obj.layerName === 'Glow')
  const appStore = useAppStore()
  const creativeService = new CreativeService()
  let visible = false
  if (!logoUrl) return

  const htmlImage = await loadHtmlImage(logoUrl)
  const logoColor = getDominantColor(htmlImage)
  if (!bgColor) return
  const bgColorRgb = hexToRgb(bgColor)
  if (!bgColorRgb) return

  const hasBackground = await creativeService.hasBackground(
    appStore.selectedBusinessProfileId,
    logoUrl,
    0.4,
    200,
  )

  const contrast = contrastRatio(logoColor, bgColorRgb)
  if (!hasBackground && contrast < 3) {
    visible = true
  } else {
    visible = false
  }
  if (item != null) {
    item.logoInfo.push({ logoUrl: logoUrl, logoRequired: visible })
  }
  if (primaryGlowObj) {
    primaryGlowObj.visible = visible
    primaryGlowObj.selectable = false
    primaryGlowObj.evented = false
  }
  if (secondaryGlowObject) {
    secondaryGlowObject.visible = false
    secondaryGlowObject.selectable = false
    secondaryGlowObject.evented = false
  }
  fabricCanvas?.renderAll()
}
